import React, { useState, useEffect } from "react";
// Replaced PhoneInput with a simple text input (India-only)
import "./LoginScreen.css";
import SalonService from "../../services/SalonService";
import { useNavigate } from "react-router-dom";
import LoadingIndicator from '../loadingIndicator/LoadingIndicator';
import { useSalon } from "../../context/SalonContext";
import { FaShieldAlt, FaChevronRight } from "react-icons/fa";

// Logo image - assuming tabImage.png is the chair logo based on mobile app screenshot
import ssbLogo from "../../assets/images/tabImage.png";

const LoginScreen = () => {
    const { login } = useSalon();
    const [customerName, setCustomerName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [otp, setOtp] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [timer, setTimer] = useState(30);
    const [showResend, setShowResend] = useState(false);
    const [loading, setLoading] = useState(false);
    const [agreed, setAgreed] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        let interval;
        if (otpSent && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else if (otpSent && timer === 0) {
            setShowResend(true);
        }
        return () => clearInterval(interval);
    }, [otpSent, timer]);

    const normalizeNumber = (num) => {
        if (!num) return "";
        let clean = num.replace(/\D/g, "");
        if (clean.startsWith("91") && clean.length > 10) {
            clean = clean.slice(2);
        }
        return clean.replace(/^0+/, "");
    };

    const sendOtp = async () => {
        if (!customerName.trim()) {
            setErrorMessage("Please enter your name");
            return;
        }

        const cleanNumber = normalizeNumber(phoneNumber);
        if (cleanNumber.length !== 10) {
            setErrorMessage("Please enter a valid 10-digit mobile number");
            return;
        }

        if (!agreed) {
            setErrorMessage("You must agree to the Terms and Privacy Policy");
            return;
        }

        setErrorMessage("");
        setLoading(true);

        const data = { mobileNumber: "+91" + cleanNumber };
        try {
            console.log("Sending OTP with data:", data);
            const response = await SalonService.sendLoginOTP(data);
            if (response.status === 200) {
                setOtpSent(true);
                setTimer(30);
                setShowResend(false);
            }
        } catch (e) {
            console.error("Error sending OTP:", e);
            setErrorMessage(e.response?.data || "Failed to send OTP");
        } finally {
            setLoading(false);
        }
    };

    const resendOtp = () => {
        sendOtp();
    };

    const verifyOtp = async () => {
        if (otp.length !== 6) {
            setErrorMessage("Please enter a valid 6-digit OTP");
            return;
        }

        const cleanNumber = normalizeNumber(phoneNumber);
        setLoading(true);
        setErrorMessage("");
        const data = {
            mobileNumber: "+91" + cleanNumber,
            otp,
            name: customerName
        };

        try {
            const response = await SalonService.customerLogin(data);
            if (response.status === 200) {
                login(
                    customerName,
                    response.data.mobileNumber,
                    response.data.accessToken,
                    response.data.customerId,
                    response.data.refreshToken
                );
                navigate("/booking", { replace: true });
            }
        } catch (e) {
            setErrorMessage(e.response?.data?.message || "Invalid OTP, please try again");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mobile-login-container">
            <div className="mobile-login-content">
                <div className="login-header-section">
                    <div className="app-logo-circle">
                        <img src={ssbLogo} alt="Salon Logo" className="app-logo-img" />
                    </div>
                    <h1 className="mobile-app-title">Salon Slot Booking</h1>
                    <p className="mobile-app-subtitle">Book your favorite salon instantly</p>
                    <div className="mobile-accent-diamond">♦</div>
                </div>

                <div className="mobile-card">
                    {loading && <div className="mobile-loading-overlay"><LoadingIndicator /></div>}

                    {!otpSent ? (
                        <div className="mobile-form">
                            <div className="mobile-input-wrapper">
                                <input
                                    type="text"
                                    className="mobile-text-input"
                                    placeholder="Enter Your Name"
                                    value={customerName}
                                    onChange={(e) => setCustomerName(e.target.value)}
                                />
                            </div>

                            <div className="mobile-phone-input-wrapper">
                                <div className="phone-container-mobile">
                                    <div className="phone-button-mobile" aria-hidden>
                                        <span className="phone-flag">🇮🇳</span>
                                        <span className="country-code">+91</span>
                                    </div>
                                    <input
                                        type="tel"
                                        className="phone-field-mobile"
                                        placeholder="Enter mobile number"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="mobile-agreement">
                                <label className="checkbox-container">
                                    <input
                                        type="checkbox"
                                        checked={agreed}
                                        onChange={() => setAgreed(!agreed)}
                                    />
                                    <span className="checkmark"></span>
                                    <span className="agreement-text">
                                        I agree to the <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>
                                    </span>
                                </label>
                            </div>

                            <button className="mobile-continue-btn" onClick={sendOtp}>
                                Continue <FaChevronRight className="mobile-chevron" />
                            </button>
                        </div>
                    ) : (
                        <div className="mobile-form">
                            <p className="mobile-otp-sent-text">Please enter the code sent to +91 {normalizeNumber(phoneNumber)}</p>
                            <div className="mobile-input-wrapper">
                                <input
                                    className="mobile-text-input otp-field"
                                    type="text"
                                    placeholder="Enter 6-Digit OTP"
                                    maxLength="6"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                />
                            </div>

                            <div className="mobile-otp-footer">
                                {showResend ? (
                                    <span className="mobile-resend-link" onClick={resendOtp}>Resend Code</span>
                                ) : (
                                    <span className="mobile-timer-text">Resend in <strong>{timer}s</strong></span>
                                )}
                            </div>

                            <button className="mobile-continue-btn" onClick={verifyOtp}>
                                Verify & Proceed <FaChevronRight className="mobile-chevron" />
                            </button>

                            <button className="mobile-back-link" onClick={() => setOtpSent(false)}>
                                Change Mobile Number
                            </button>
                        </div>
                    )}

                    {errorMessage && <p className="mobile-error-msg">{errorMessage}</p>}

                    <div className="mobile-security-info">
                        <div className="security-icon-circle">
                            <FaShieldAlt />
                        </div>
                        <div className="security-labels">
                            <span className="security-title">Secure • Fast • Reliable</span>
                            <span className="security-desc">Your data is always protected</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginScreen;
